/**
 * @fileoverview Императивный DOM HUD: списки, карточка, полосы, тосты, оверлеи.
 */

import { Snd } from "@/game/audio/sound";
import { AIRLINE_NAME, type AirlineCode } from "@/game/constants/aircraft";
import {
  SCENARIOS,
  type ScenarioId,
} from "@/game/constants/scenarios";
import { MODES, type ModeId } from "@/game/constants/modes";
import { ST_LABEL, ST_SHORT } from "@/game/constants/status";
import {
  bestScore,
  endlessRec,
  MAX_VIOL,
  MODE_KEY,
  saveBest,
  saveEndlessMeta,
  TUT_KEY,
} from "@/game/constants/world";
import {
  canLand,
  canTakeoff,
  cmdClearSnow,
  cmdGate,
  cmdGoAround,
  cmdHold,
  cmdLand,
  cmdResolveHazard,
  cmdTakeoff,
  cmdTaxiOut,
  cmdTreatIce,
} from "@/game/commands/ops";
import { cmdDeice } from "@/game/systems/deice";
import { buildDebrief } from "@/game/systems/debrief";
import type { GameHooks, GameState, Plane } from "@/game/state/types";
import { clamp, fmtTime } from "@/game/util/math";
import type { Session } from "@/game/session/Session";

const TUT = [
  "Коснитесь борта <b>на подходе</b> — на схеме или в списке слева.",
  "Отлично. Теперь выдайте команду <b>ПОСАДКА</b> на любую свободную полосу.",
  "После посадки назначьте <b>свободный гейт</b> — можно заранее, пока борт еще сходит с полосы.",
  "Последнее: справа внизу нажмите <b>Снег</b>. Кнопка <b>Реагент</b> станет доступна позже, когда появится гололед.",
  "Готово! Следите за «Безопасностью» в шапке — это риск остановки смены, не ваш личный рейтинг.",
];

type El = HTMLElement;

function $(sel: string, root: ParentNode): El | null {
  return root.querySelector(sel);
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function patColor(v: number): string {
  return v > 0.5 ? "#7ee08a" : v > 0.25 ? "#ffc861" : "#ff6b6b";
}

/** Цвет шкалы терпения экипажа (не «готовность» — без зеленого). */
function patienceColor(v: number): string {
  return v > 0.5 ? "#8eb4d4" : v > 0.25 ? "#ffc861" : "#ff6b6b";
}

function meterColor(status: string, v: number): string {
  if (status === "service") return "#5fd7ff";
  if (status === "approach") return patColor(v);
  return patienceColor(v);
}

function meterLabel(status: string, p: { svcLeft: number }): string {
  if (status === "service")
    return `обслуживание · осталось ${Math.ceil(p.svcLeft)}с`;
  if (status === "approach") return "запас топлива";
  if (status === "awaiting_gate") return "терпение экипажа · ждет гейт";
  if (status === "ready") return "терпение экипажа · ждет выруливание";
  if (status === "hold_short") return "терпение экипажа · ждет взлет";
  return "терпение экипажа";
}

function nextAction(
  p: Plane,
): { label: string; detail: string; tone: "act" | "wait" | "risk" } {
  const needsDeice = p.needsDeice && !p.deiced && !p.deiceWaive;
  if (needsDeice && ["service", "ready", "hold_short"].includes(p.status)) {
    return {
      label: "Обработайте от обледенения",
      detail: "Команда находится ниже в карточке борта",
      tone: "act",
    };
  }
  switch (p.status) {
    case "approach":
      return { label: "Разрешите посадку", detail: "Выберите свободную полосу", tone: "act" };
    case "inbound":
    case "landing":
      return { label: "Дождитесь посадки", detail: "Борт выполняет команду", tone: "wait" };
    case "vacating":
    case "awaiting_gate":
      return { label: "Назначьте гейт", detail: "Выберите любой свободный гейт", tone: "act" };
    case "taxi_gate":
      return { label: "Дождитесь прибытия к гейту", detail: "Борт выполняет команду", tone: "wait" };
    case "service":
      return { label: "Дождитесь обслуживания", detail: `Осталось около ${Math.ceil(p.svcLeft)} с`, tone: "wait" };
    case "ready":
      return { label: "Отправьте борт на полосу", detail: "Нажмите «Выруливание»", tone: "act" };
    case "taxi_rwy":
      return { label: "Дождитесь полосы", detail: "Борт выполняет команду", tone: "wait" };
    case "hold_short":
      return { label: "Разрешите взлет", detail: "Проверьте состояние полосы", tone: "act" };
    case "takeoff":
      return { label: "Дождитесь взлета", detail: "Борт выполняет команду", tone: "wait" };
    case "goaround":
      return { label: "Дождитесь возвращения", detail: "Борт ушел на второй круг", tone: "risk" };
  }
}

/**
 * DOM-оболочка, привязанная к Session. Вызовите {@link DomShell.bind} один раз после вставки разметки.
 */
export class DomShell {
  private root: ParentNode;
  private session: Session;
  private onStartMatch: (tutorial: boolean) => void;
  private pendingConfirm: (() => void) | null = null;
  private cardKey = "";
  private tutTimer: ReturnType<typeof setTimeout> | null = null;
  private uiT = 0;
  private arrKey = "";
  private depKey = "";
  private rwKey = "";
  private logItems: HTMLElement[] = [];

  private ui!: {
    time: El;
    timeBox: El;
    timeLbl: El;
    score: El;
    best: El;
    safe: El;
    safeBox: El;
    front: El;
    wx: El;
    arr: El;
    dep: El;
    cArr: El;
    cDep: El;
    rw: El;
    card: El;
    cardTag: El;
  };

  constructor(
    root: ParentNode,
    session: Session,
    onStartMatch: (tutorial: boolean) => void,
  ) {
    this.root = root;
    this.session = session;
    this.onStartMatch = onStartMatch;
  }

  /** Привязать кнопки и закэшировать узлы HUD. */
  bind(): void {
    const r = this.root;
    this.ui = {
      time: $("#sTime", r)!,
      timeBox: $("#sTimeBox", r)!,
      timeLbl: $("#sTimeLbl", r)!,
      score: $("#sScore", r)!,
      best: $("#sBest", r)!,
      safe: $("#sSafe", r)!,
      safeBox: $("#sSafeBox", r)!,
      front: $("#sFront", r)!,
      wx: $("#sWx", r)!,
      arr: $("#listArr", r)!,
      dep: $("#listDep", r)!,
      cArr: $("#cArr", r)!,
      cDep: $("#cDep", r)!,
      rw: $("#rwBox", r)!,
      card: $("#card", r)!,
      cardTag: $("#cardTag", r)!,
    };

    this.renderModes();
    this.renderScenarios();
    this.updateBestUI();
    $("#rotate", r)?.classList.add("show");

    const click = (sel: string, fn: () => void): void => {
      $(sel, r)?.addEventListener("click", fn);
    };

    click("#bPlay", () => {
      Snd.init();
      Snd.resume();
      Snd.click();
      this.startMatch(false);
    });
    click("#bTut", () => {
      Snd.init();
      Snd.resume();
      Snd.click();
      this.startMatch(true);
    });
    click("#bAgain", () => {
      Snd.click();
      this.startMatch(false);
    });
    click("#bMenu", () => {
      Snd.click();
      $("#ovOver", r)?.classList.add("hide");
      $("#ovMenu", r)?.classList.remove("hide");
      const g = this.session.g;
      if (g) g.running = false;
    });
    click("#bResume", () => {
      Snd.click();
      this.pauseGame(false);
    });
    click("#bQuit", () => {
      Snd.click();
      $("#ovPause", r)?.classList.add("hide");
      this.endGame("Смена прервана", "Вы завершили дежурство досрочно.");
    });
    click("#bPause", () => {
      Snd.init();
      Snd.click();
      const g = this.session.g;
      if (g && !g.over) this.pauseGame(g.running);
    });
    click("#bRestart", () => {
      Snd.init();
      Snd.click();
      this.startMatch(false);
    });
    click("#bHelp", () => this.openHelp());
    click("#bHelpMenu", () => {
      Snd.init();
      this.openHelp();
    });
    click("#bHelpClose", () => this.closeHelp());
    click("#bLogOpen", () => this.openLog());
    click("#bLogClose", () => this.closeLog());
    this.bindHelpNav();
    click("#cfYes", () => {
      Snd.click();
      this.resolveConfirm(true);
    });
    click("#cfNo", () => {
      Snd.click();
      this.resolveConfirm(false);
    });
    click("#tutSkip", () => {
      Snd.init();
      Snd.click();
      this.clearTutTimer();
      const g = this.session.g;
      if (g?.mode === "tutorial") this.startMatch(false);
      else $("#tut", r)?.classList.add("hide");
    });
    click("#rotClose", () => $("#rotate", r)?.classList.remove("show"));

    const soundBtn = $("#bSound", r);
    soundBtn?.addEventListener("click", () => {
      Snd.init();
      Snd.resume();
      const on = !Snd.isOn;
      Snd.set(on);
      soundBtn.classList.toggle("off", !on);
      const w1 = document.getElementById("wave1");
      const w2 = document.getElementById("wave2");
      const m = document.getElementById("mute");
      if (w1) w1.style.display = on ? "" : "none";
      if (w2) w2.style.display = on ? "" : "none";
      if (m) m.style.display = on ? "none" : "";
    });

    document.addEventListener("keydown", this.onKey);
    document.addEventListener("visibilitychange", this.onVis);
  }

  dispose(): void {
    document.removeEventListener("keydown", this.onKey);
    document.removeEventListener("visibilitychange", this.onVis);
    this.clearTutTimer();
  }

  /** Реализация GameHooks для симуляции. */
  hooks(): GameHooks {
    return {
      toast: (t, k) => this.toast(t, k),
      fx: (x, y, txt, col) => {
        const g = this.session.g;
        if (g) g.floats.push({ x, y, txt, col, t: 1.5 });
      },
      askConfirm: (text, onYes) => this.askConfirm(text, onYes),
      endGame: (title, sub) => this.endGame(title, sub),
      tutNext: () => this.tutNext(),
      onScoreChanged: (s) => {
        this.ui.score.textContent = String(s);
      },
      onWxName: (n) => {
        this.ui.wx.textContent = n;
      },
      invalidateCard: () => {
        this.cardKey = "";
      },
      selectPlane: (p) => this.selectPlane(p, true),
      showDecision: (ev) => this.showDecision(ev),
    };
  }

  /** Периодическое обновление HUD (из rAF). */
  tickHud(dt: number, frontLabel: string): void {
    const g = this.session.g;
    if (!g) return;
    this.uiT += dt;
    if (g.cfg.endless) {
      this.ui.timeLbl.textContent = "Смена";
      this.ui.time.textContent = `${fmtTime(g.timeAlive)} · в${g.front.wave}`;
      this.ui.timeBox.classList.remove("low");
    } else if (g.mode === "play") {
      this.ui.timeLbl.textContent = "Смена";
      this.ui.time.textContent = fmtTime(g.time);
      this.ui.timeBox.classList.toggle("low", g.time <= 30);
    }
    this.ui.safe.textContent = String(g.safety);
    this.ui.safeBox.classList.toggle("mid", g.safety < 70 && g.safety >= 40);
    this.ui.safeBox.classList.toggle("low", g.safety < 40);
    this.ui.front.textContent = frontLabel;
    this.ui.score.textContent = String(g.score);
    this.ui.wx.textContent = g.wxName;
    const endlessLoop = (g.timeAlive % 720) / 360;
    const dayPhase = g.cfg.endless
      ? endlessLoop <= 1 ? endlessLoop : 2 - endlessLoop
      : clamp(g.timeAlive / Math.max(1, g.cfg.time), 0, 1);
    const beforeNight = dayPhase < 0.45;
    const dayK = beforeNight
      ? 1 - dayPhase / 0.45
      : (dayPhase - 0.45) / 0.55;
    const app = $("#app", this.root);
    app?.style.setProperty(
      "--day-tint",
      beforeNight
        ? `rgba(122,62,48,${(0.12 * dayK).toFixed(3)})`
        : `rgba(145,105,78,${(0.16 * dayK).toFixed(3)})`,
    );

    if (this.uiT > 0.14) {
      this.uiT = 0;
      this.renderLists();
      this.renderRunways();
      this.renderCard();
    }
    $("#flash", this.root)?.classList.toggle("on", g.flash > 0.25);
  }

  selectPlane(p: Plane | null, quiet = false): void {
    if (!quiet) {
      Snd.init();
      Snd.click();
    }
    this.session.select(p);
    if (p?.status === "approach") {
      p.joining = false;
      p.x = clamp(p.x, 120, 1480);
      p.y = clamp(p.y, 40, 170);
    }
    this.cardKey = "";
    this.arrKey = "";
    this.depKey = "";
    this.renderCard(true);
    const g = this.session.g;
    if (!quiet && g && g.tutStep === 0 && p?.tut) this.tutNext();
  }

  /** Журнал событий (правая колонка `#log`). */
  toast(msg: string, kind?: string): void {
    const logBox = $("#log", this.root);
    if (!logBox) return;
    const g = this.session.g;
    const el = document.createElement("div");
    el.className = "logitem" + (kind ? ` ${kind}` : "");
    let t = "";
    if (g) {
      t = fmtTime(
        Math.max(
          0,
          g.cfg.endless
            ? g.timeAlive
            : g.mode === "play"
              ? g.time
              : g.cfg.time || 180,
        ),
      );
    }
    el.innerHTML = `<i></i><span>${msg}</span><span class="tm">${t}</span>`;
    logBox.insertBefore(el, logBox.firstChild);
    this.logItems.unshift(el);
    while (this.logItems.length > 28) {
      const o = this.logItems.pop();
      o?.remove();
    }
    if (!$("#ovLog", this.root)?.classList.contains("hide")) {
      this.syncLogModal();
    }
    const life = kind === "bad" || kind === "wx" ? 90000 : 70000;
    setTimeout(() => {
      if (!el.parentNode) return;
      el.style.transition = "opacity .4s";
      el.style.opacity = "0.42";
    }, Math.min(22000, life * 0.35));
  }

  /** Карточка решения из EVENT_DECK. */
  showDecision(ev: {
    id: string;
    title: string;
    text: string;
    opts: ReadonlyArray<{
      label: string;
      tip: string;
      run: (ctx: { g: GameState; hooks: GameHooks }) => void;
    }>;
  }): void {
    const g = this.session.g;
    if (!g) return;
    const title = $("#evTitle", this.root);
    const text = $("#evText", this.root);
    const box = $("#evOpts", this.root);
    if (title) title.textContent = ev.title;
    if (text) text.textContent = ev.text;
    if (box) {
      box.innerHTML = "";
      for (const o of ev.opts) {
        const b = document.createElement("button");
        const tip = o.tip.toLocaleLowerCase("ru");
        const tone = tip.includes("риск") || tip.includes("видимость")
          ? " risk"
          : tip.includes("безопас")
            ? " go"
            : "";
        b.className = `btn${tone}`;
        b.type = "button";
        b.innerHTML = `<span class="dot"></span>${o.label}${o.tip ? `<small>${o.tip}</small>` : ""}`;
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          e.stopPropagation();
          Snd.click();
          try {
            o.run({ g, hooks: this.hooks() });
          } catch {
            /* игнор */
          }
          g.decision = null;
          $("#ovEvent", this.root)?.classList.add("hide");
          if (!g.over) g.running = true;
          g.eventT = (28 + Math.random() * 18) / g.cfg.events;
          this.cardKey = "";
          this.rwKey = "";
          this.renderCard(true);
          this.renderRunways();
        });
        box.appendChild(b);
      }
    }
    $("#ovEvent", this.root)?.classList.remove("hide");
  }

  private startMatch(tutorial: boolean): void {
    try {
      localStorage.setItem(TUT_KEY, "1");
    } catch {
      /* игнор */
    }
    this.clearTutTimer();
    this.clearToasts();
    this.clearOverlays();
    this.onStartMatch(tutorial);
    $("#tut", this.root)?.classList.toggle("hide", !tutorial);
    $("#ovMenu", this.root)?.classList.add("hide");
    if (tutorial) this.tutShow();
    else {
      const sc = this.session.scenarioId;
      const name = SCENARIOS.find((s) => s.id === sc)?.label;
      this.toast(
        sc && sc !== "free"
          ? `Сценарий «${name}». Удачной ночи, диспетчер.`
          : "Смена началась. Удачной ночи, диспетчер.",
        "good",
      );
    }
    this.cardKey = "";
    this.rwKey = "";
    this.renderAll();
  }

  private renderAll(): void {
    this.renderLists();
    this.rwKey = "";
    this.renderRunways();
    this.renderCard(true);
    const g = this.session.g;
    if (!g) return;
    this.ui.score.textContent = String(g.score);
    this.ui.wx.textContent = g.wxName;
  }

  private renderModes(): void {
    const box = $("#modeBox", this.root);
    if (!box) return;
    box.innerHTML = "";
    for (const m of Object.values(MODES)) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "mode" + (this.session.modeId === m.id ? " on" : "");
      const rec = bestScore(m.id);
      let extra = rec ? `Рекорд ${rec}` : "";
      if (m.endless) {
        const meta = endlessRec();
        if (meta.waves) extra += `${extra ? " · " : ""}волны ${meta.waves}`;
      }
      b.innerHTML = `<b>${m.label}</b><small>${m.sub}</small>${extra ? `<div class="rec">${extra}</div>` : ""}`;
      b.addEventListener("click", () => {
        this.session.setMode(m.id as ModeId);
        try {
          localStorage.setItem(MODE_KEY, m.id);
        } catch {
          /* игнор */
        }
        this.updateBestUI();
        this.renderModes();
        Snd.click();
      });
      box.appendChild(b);
    }
  }

  private renderScenarios(): void {
    const box = $("#scenarioBox", this.root);
    if (!box) return;
    box.innerHTML = "";
    for (const s of SCENARIOS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className =
        "mode" + (this.session.scenarioId === s.id ? " on" : "");
      b.innerHTML = `<b>${s.label}</b><small>${s.sub}</small>`;
      b.addEventListener("click", () => {
        this.session.setScenario(s.id as ScenarioId);
        this.renderScenarios();
        Snd.click();
      });
      box.appendChild(b);
    }
  }

  private updateBestUI(): void {
    this.ui.best.textContent = String(this.session.best());
  }

  private renderLists(): void {
    const g = this.session.g;
    if (!g) return;
    const selected = this.session.selected;
    const arr = g.planes.filter(
      (p) =>
        !p.dead &&
        [
          "approach",
          "inbound",
          "landing",
          "goaround",
          "vacating",
          "awaiting_gate",
          "taxi_gate",
        ].includes(p.status),
    );
    const dep = g.planes.filter(
      (p) =>
        !p.dead &&
        ["service", "ready", "taxi_rwy", "hold_short", "takeoff"].includes(
          p.status,
        ),
    );
    this.ui.cArr.textContent = String(arr.length);
    this.ui.cDep.textContent = String(dep.length);

    const paint = (
      box: El,
      list: Plane[],
      keyRef: "arrKey" | "depKey",
      subf: (p: Plane) => string,
    ): void => {
      const key = list
        .map(
          (p) =>
            `${p.id}:${p.status}:${selected === p ? 1 : 0}:${p.priority ? 1 : 0}:${p.status === "service" ? Math.ceil(p.svcLeft) : Math.round(p.pat * 4)}`,
        )
        .join("|");
      if (this[keyRef] === key) {
        list.forEach((p, i) => {
          const row = box.children[i] as HTMLElement | undefined;
          if (!row) return;
          const bar = row.querySelector(".bar > i") as HTMLElement | null;
          if (bar) {
            bar.style.width = `${(clamp(p.pat, 0, 1) * 100).toFixed(0)}%`;
            bar.style.background = meterColor(p.status, p.pat);
          }
          const st = row.querySelector(".st");
          if (st) st.textContent = subf(p);
        });
        return;
      }
      this[keyRef] = key;
      box.innerHTML = "";
      if (!list.length) {
        box.innerHTML = '<div class="nodata">нет бортов</div>';
        return;
      }
      for (const p of list) {
        const el = document.createElement("button");
        el.className =
          "flight" +
          (selected === p ? " sel" : "") +
          (p.priority ? " prio" : "");
        el.type = "button";
        const bar = [
          "approach",
          "awaiting_gate",
          "ready",
          "hold_short",
          "service",
        ].includes(p.status);
        const code = (p.cs || "").split(" ")[0] as AirlineCode;
        const an = AIRLINE_NAME[code] || code;
        el.title = `${an} · ${p.cs} · ${ST_LABEL[p.status] || p.status}`;
        el.innerHTML =
          (p.priority ? '<span class="tagp">ПРИО</span>' : "") +
          `<div class="r1"><span class="cs">${p.cs}</span><span class="tp">${p.T.label}</span></div>` +
          `<div class="st">${subf(p)}</div>` +
          (bar
            ? `<div class="bar" title="${meterLabel(p.status, p)}"><i style="width:${(clamp(p.pat, 0, 1) * 100).toFixed(0)}%;background:${meterColor(p.status, p.pat)}"></i></div>`
            : "");
        el.addEventListener("click", () => this.selectPlane(p));
        box.appendChild(el);
      }
    };

    paint(this.ui.arr, arr, "arrKey", (p) => {
      let s = ST_SHORT[p.status] || p.status;
      if (p.status === "approach") s = "ждет посадки →";
      if (p.status === "awaiting_gate") s = "нужен гейт →";
      if (p.status === "taxi_gate" && p.gate !== null)
        s = `едет к ${g.gates[p.gate]!.name}`;
      return s;
    });
    paint(this.ui.dep, dep, "depKey", (p) => {
      let s = ST_SHORT[p.status] || p.status;
      if (p.status === "service")
        s = `у гейта ${p.gate !== null ? g.gates[p.gate]!.name : ""} · ${Math.ceil(p.svcLeft)}с`;
      if (p.status === "ready") s = "готов → на полосу";
      if (p.status === "hold_short" && p.rwyWish != null)
        s = `у ${g.runways[p.rwyWish]!.name} → взлет`;
      return s;
    });
  }

  private renderRunways(): void {
    const g = this.session.g;
    if (!g) return;
    const hooks = this.hooks();
    const hz = g.hazards.length;
    const deiceNeed = g.planes.filter(
      (p) =>
        !p.dead &&
        p.needsDeice &&
        !p.deiced &&
        !p.deiceWaive &&
        !p.deiceT &&
        (p.status === "service" ||
          p.status === "ready" ||
          p.status === "hold_short"),
    ).length;
    const lock = Math.ceil(g.clearLockT || 0);
    const key =
      g.runways
        .map(
          (r) =>
            `${Math.round(r.snow)}:${Math.round(r.ice || 0)}:${r.plane ? 1 : 0}:${r.closed ? 1 : 0}:${Math.ceil(r.clearSnowT || 0)}:${Math.ceil(r.clearIceT || 0)}:${Math.ceil(r.reserveT || 0)}`,
        )
        .join("|") +
      `:${Math.round(g.reagent)}:${hz}:${deiceNeed}:${lock}:${Math.round(g.windSpd)}:${g.cross ? 1 : 0}:${Math.ceil(g.takeoffLockT || 0)}`;
    if (this.rwKey === key) return;
    this.rwKey = key;
    const box = this.ui.rw;
    box.innerHTML = "";

    if (hz > 0 || deiceNeed > 0) {
      const alert = document.createElement("div");
      alert.className = "opsAlert";
      if (hz > 0) {
        const em = document.createElement("button");
        em.className = "btn risk";
        em.type = "button";
        em.innerHTML =
          '<span class="dot"></span>СНЯТЬ УГРОЗУ<small>сейчас</small>';
        em.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          Snd.click();
          cmdResolveHazard(g, hooks);
          this.rwKey = "";
          this.renderRunways();
        });
        alert.appendChild(em);
      }
      if (deiceNeed > 0) {
        const p = g.planes.find(
          (x) =>
            !x.dead &&
            x.needsDeice &&
            !x.deiced &&
            !x.deiceWaive &&
            !x.deiceT &&
            (x.status === "service" ||
              x.status === "ready" ||
              x.status === "hold_short"),
        );
        const db = document.createElement("button");
        db.className = "btn warn";
        db.type = "button";
        db.innerHTML = `<span class="dot"></span>Обработка · ${p ? p.cs : "БОРТ"}<small>самолет</small>`;
        db.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          Snd.click();
          if (p) {
            this.selectPlane(p, true);
            cmdDeice(g, hooks, p);
            this.renderCard(true);
          }
          this.rwKey = "";
          this.renderRunways();
        });
        alert.appendChild(db);
      }
      box.appendChild(alert);
    }

    const grid = document.createElement("div");
    grid.className = "opsRow";
    const otherClear = g.runways.some((r) => r.clearSnowT > 0);
    g.runways.forEach((r, i) => {
      const d = document.createElement("div");
      d.className = "rw";
      let stateLabel: string;
      let stateClass: "ok" | "busy" | "closed";
      if (r.clearSnowT > 0) {
        stateLabel = `ЧИСТИМ ${Math.ceil(r.clearSnowT)}С`;
        stateClass = "closed";
      } else if (r.clearIceT > 0) {
        stateLabel = "РЕАГЕНТ";
        stateClass = "busy";
      } else if ((r.reserveT || 0) > 0) {
        stateLabel = `MED ${Math.ceil(r.reserveT)}С`;
        stateClass = "busy";
      } else if (r.closed) {
        stateLabel = "ЗАКРЫТА";
        stateClass = "closed";
      } else if (r.plane) {
        stateLabel = r.plane.cs;
        stateClass = "busy";
      } else {
        stateLabel = "СВОБОДНА";
        stateClass = "ok";
      }
      d.classList.add(`state-${stateClass}`);
      const snowNeed = r.snow >= 8;
      const iceNeed = (r.ice || 0) >= 6;
      const iceRisk = (r.ice || 0) >= 40 || r.snow >= 62;
      d.innerHTML = `<div class="rw-head"><strong>${r.name}</strong><span class="rw-state ${stateClass}">${stateLabel}</span></div>
        <div class="rw-data">
          <span><i class="snow-key"></i>СНЕГ <b>${Math.round(r.snow)}%</b></span>
          <span><i class="ice-key"></i>${(r.reserveT || 0) > 0 ? `РЕЗЕРВ <b>${r.reserveLabel || "MED"}</b>` : `ГОЛОЛЕД <b>${Math.round(r.ice || 0)}%${iceRisk ? " · РИСК" : ""}</b>`}</span>
        </div>
        <div class="meters"><div class="snowbar"><i style="width:${Math.round(r.snow)}%"></i></div><div class="icebar"><i style="width:${Math.round(r.ice || 0)}%"></i></div></div>`;
      const row = document.createElement("div");
      row.className = "btn2";
      const canSnow =
        snowNeed &&
        !r.plane &&
        !(r.clearSnowT > 0) &&
        lock <= 0 &&
        !(otherClear && !(r.clearSnowT > 0));
      const b = document.createElement("button");
      b.className = "btn" + (snowNeed ? " warn" : "");
      b.type = "button";
      b.disabled = !canSnow;
      b.title =
        "Убрать снег с полосы. Пока идет уборка — полоса закрыта.";
      b.innerHTML = '<span class="dot"></span>Снег';
      b.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        Snd.init();
        Snd.click();
        cmdClearSnow(g, hooks, i);
        this.rwKey = "";
        this.renderRunways();
      });
      const br = document.createElement("button");
      br.className = "btn" + (iceNeed ? " warn" : "");
      br.type = "button";
      br.disabled = r.clearIceT > 0 || g.reagent < 12 || !iceNeed;
      br.title = !iceNeed
        ? "Реагент нужен при гололеде ≥6% (см. полоску льда). Это не обработка самолета."
        : g.reagent < 12
          ? `Мало запаса реагента (${Math.round(g.reagent)}%).`
          : "Снять гололед с полосы (−12% запаса реагента).";
      br.innerHTML = '<span class="dot"></span>Реагент';
      br.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        Snd.init();
        Snd.click();
        cmdTreatIce(g, hooks, i);
        this.rwKey = "";
        this.renderRunways();
      });
      row.appendChild(b);
      row.appendChild(br);
      d.appendChild(row);
      grid.appendChild(d);
    });
    box.appendChild(grid);
  }

  renderCard(force = false): void {
    const g = this.session.g;
    const p = this.session.selected;
    const hooks = this.hooks();
    if (!g) return;
    const key = p
      ? `${p.id}:${p.status}:${p.needsDeice && !p.deiced && !p.deiceWaive ? 1 : 0}:${p.deiceWaive ? 1 : 0}:${Math.ceil(p.deiceT || 0)}:${Math.ceil(g.takeoffLockT || 0)}:${g.runways.map((r) => (r.plane ? 1 : 0) + (r.closed ? 2 : 0) + Math.round(r.snow / 10) + (r.reservedFor ? 4 : 0)).join("")}${g.gates.map((gate) => (gate.plane ? 1 : 0)).join("")}`
      : "none";
    if (!force && this.cardKey === key) {
      const bar = this.ui.card.querySelector(".bar > i") as HTMLElement | null;
      if (
        bar &&
        p &&
        ["approach", "awaiting_gate", "ready", "hold_short", "service"].includes(
          p.status,
        )
      ) {
        bar.style.width = `${clamp(p.pat, 0, 1) * 100}%`;
        bar.style.background = meterColor(p.status, p.pat);
        const lab = this.ui.card.querySelector(".meter-lab") as HTMLElement | null;
        if (lab) lab.textContent = meterLabel(p.status, p);
      }
      return;
    }
    this.cardKey = key;
    const card = this.ui.card;
    card.innerHTML = "";
    if (!p) {
      this.ui.cardTag.textContent = "не выбран";
      card.innerHTML =
        '<div class="nodata">Выберите самолет на схеме<br>или в списке слева — здесь появятся команды</div>';
      return;
    }
    this.ui.cardTag.textContent = ST_SHORT[p.status] || p.status;
    const code = (p.cs || "").split(" ")[0] as AirlineCode;
    const an = AIRLINE_NAME[code] || code;
    const head = document.createElement("div");
    const deiceChip =
      p.deiceT > 0
        ? `<span class="chip" style="color:#c8aaff">обработка… ${Math.ceil(p.deiceT)}с</span>`
        : p.deiceWaive
          ? '<span class="chip" style="color:#ff9a9a">без обработки</span>'
          : p.needsDeice && !p.deiced
            ? '<span class="chip" style="color:#ffc861">нужна обработка</span>'
            : p.deiced
              ? '<span class="chip" style="color:#7ee08a">обработка ок</span>'
              : "";
    head.innerHTML = `<div class="cs">${p.cs}</div><div class="meta">
      <span class="chip">${an}</span>
      <span class="chip">${p.T.label}</span>
      <span class="chip">${p.FK.label}</span>
      ${p.priority ? '<span class="chip priority">приоритет</span>' : ""}
      ${deiceChip}
      ${p.passengers ? `<span class="chip">${p.passengers} пасс</span>` : ""}
      <span class="chip score-chip">${p.T.pts} очк</span>
      ${p.rwy !== null ? `<span class="chip">${g.runways[p.rwy]!.name}</span>` : ""}
      ${p.gate !== null ? `<span class="chip">гейт ${g.gates[p.gate]!.name}</span>` : ""}
      ${p.status === "ready" || p.status === "hold_short" ? '<span class="chip" style="color:#7ee08a">готов к вылету</span>' : ""}
      </div><div class="lbl" style="margin-top:8px">статус</div>
      <div style="font-size:13px;color:var(--ink2);font-weight:600;line-height:1.35">${ST_LABEL[p.status] || p.status}</div>`;
    card.appendChild(head);

    const next = nextAction(p);
    const nextBox = document.createElement("div");
    nextBox.className = `next-action ${next.tone}`;
    nextBox.innerHTML = `<span>СЛЕДУЮЩЕЕ ДЕЙСТВИЕ</span><b>${next.label}</b><small>${next.detail}</small>`;
    card.appendChild(nextBox);

    if (
      ["approach", "awaiting_gate", "ready", "hold_short", "service"].includes(
        p.status,
      )
    ) {
      const lab = document.createElement("div");
      lab.className = "lbl meter-lab";
      lab.textContent = meterLabel(p.status, p);
      const bar = document.createElement("div");
      bar.className = "bar";
      bar.style.height = "6px";
      bar.title =
        p.status === "service"
          ? "Прогресс обслуживания у гейта"
          : p.status === "approach"
            ? "Топливо в зоне ожидания"
            : "Давление задержки — не шкала готовности борта";
      bar.innerHTML = `<i style="width:${clamp(p.pat, 0, 1) * 100}%;background:${meterColor(p.status, p.pat)}"></i>`;
      card.appendChild(lab);
      card.appendChild(bar);
    }

    const acts = document.createElement("div");
    acts.className = "lbl";
    acts.textContent = "команды";
    card.appendChild(acts);
    const box = document.createElement("div");
    card.appendChild(box);

    const btn = (
      label: string,
      cls: string,
      disabled: boolean,
      tip: string,
      fn: () => void,
    ): void => {
      const b = document.createElement("button");
      const primary = ["ПОСАДКА", "ГЕЙТ", "ВЫРУЛИВАНИЕ", "ВЗЛЕТ", "ОБРАБОТАТЬ"]
        .some((prefix) => label.startsWith(prefix));
      b.className = `btn ${cls}${primary ? " command-primary" : ""}`;
      b.type = "button";
      b.disabled = disabled;
      b.innerHTML = `<span class="dot"></span>${label}${tip ? `<small>${tip}</small>` : ""}`;
      if (!disabled) {
        // pointerdown надежнее click: карточка может перерисоваться до mouseup
        const run = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          if (b.disabled) return;
          Snd.init();
          Snd.resume();
          Snd.click();
          fn();
          this.cardKey = "";
          this.renderCard(true);
        };
        b.addEventListener("pointerdown", run);
        b.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }
      box.appendChild(b);
    };

    const info = (txt: string): void => {
      const d = document.createElement("div");
      d.className = "nodata";
      d.style.textAlign = "left";
      d.style.padding = "4px 2px";
      d.textContent = txt;
      box.appendChild(d);
    };

    /** Кнопка/статус противообледенительной обработки борта. */
    const deiceControls = (): void => {
      if (p.deiceT > 0) {
        info(`Обработка: ${Math.ceil(p.deiceT)} с`);
        return;
      }
      if (p.deiced) {
        info("Обработка от обледенения выполнена");
        return;
      }
      if (p.deiceWaive) {
        info("Вылет без обработки разрешен — риск повышен");
        return;
      }
      // У гейта — всегда можно обработать; перед вылетом кнопка обязательна при needsDeice
      const atGate = p.status === "service";
      const required = p.needsDeice;
      if (atGate || required) {
        btn(
          "ОБРАБОТАТЬ ОТ ОБЛЕДЕНЕНИЯ",
          "warn",
          false,
          required ? "иначе взлет запрещен" : "у гейта · перед вылетом",
          () => cmdDeice(g, hooks, p),
        );
      }
    };

    if (p.status === "approach") {
      g.runways.forEach((r, i) => {
        const c = canLand(p, r);
        btn(
          `ПОСАДКА · ${r.name}`,
          c.risky ? "risk" : "go",
          !c.ok,
          c.why || `занос ${Math.round(r.snow)}%`,
          () => cmdLand(g, hooks, p, i),
        );
      });
      btn(
        "ЭШЕЛОН ОЖИДАНИЯ",
        "warn",
        p.holdBonus > 0,
        "экономия топлива",
        () => cmdHold(g, hooks, p),
      );
      btn("НА ВТОРОЙ КРУГ", "risk", false, "−75", () =>
        cmdGoAround(g, hooks, p),
      );
    } else if (p.status === "inbound" || p.status === "landing") {
      btn(
        "ПРЕРВАТЬ ЗАХОД",
        "risk",
        p.status === "landing",
        "−75",
        () => cmdGoAround(g, hooks, p),
      );
    } else if (p.status === "awaiting_gate" || p.status === "vacating") {
      const svcEst = Math.ceil(p.T.svc);
      if (p.status === "vacating") {
        info("Можно назначить гейт заранее — борт свернет сам");
      }
      info(
        `Гейты A1–B2 одинаковые. ${p.T.label} займет у любого гейта ~${svcEst} с`,
      );
      g.gates.forEach((gate, i) => {
        const occ = gate.plane && !gate.plane.dead && gate.plane !== p ? gate.plane : null;
        const busy = !!occ;
        const chosen = p.gate === i || p.pendingGate === i;
        let tip = `свободен · обслуж. ~${svcEst} с`;
        if (chosen) tip = "выбран · борт едет";
        if (occ) {
          if (occ.status === "service") tip = `занят ${occ.cs} · еще ${Math.ceil(occ.svcLeft)} с`;
          else if (occ.status === "taxi_gate") tip = `занят ${occ.cs} · едет`;
          else if (occ.status === "ready") tip = `занят ${occ.cs} · готов к вылету`;
          else tip = `занят ${occ.cs}`;
        }
        btn(
          `ГЕЙТ ${gate.name}${chosen ? " · ВЫБРАН" : ""}`,
          chosen ? "selected-choice" : busy ? "" : "go",
          busy || chosen,
          tip,
          () => cmdGate(g, hooks, p, i),
        );
      });
    } else if (p.status === "taxi_gate") {
      const gate = p.gate !== null ? g.gates[p.gate] : null;
      if (gate) {
        btn(
          `ГЕЙТ ${gate.name} · ВЫБРАН`,
          "selected-choice",
          true,
          "борт едет",
          () => {},
        );
      }
      info("Борт выполняет команду");
    } else if (p.status === "service") {
      deiceControls();
      info(`Обслуживание: еще ${Math.ceil(p.svcLeft)} с · ${p.T.label}`);
    } else if (p.status === "ready") {
      deiceControls();
      g.runways.forEach((r, i) => {
        btn(
          `ВЫРУЛИВАНИЕ · ${r.name}`,
          "go",
          false,
          `занос ${Math.round(r.snow)}%`,
          () => cmdTaxiOut(g, hooks, p, i),
        );
      });
    } else if (p.status === "hold_short") {
      deiceControls();
      const c = canTakeoff(g, p);
      btn(
        `ВЗЛЕТ · ${g.runways[p.rwyWish!]!.name}`,
        c.risky ? "risk" : "go",
        !c.ok,
        c.why || "полоса чистая",
        () => cmdTakeoff(g, hooks, p),
      );
      info("Удержание у полосы — борт ждет вашей команды");
    } else {
      const d = document.createElement("div");
      d.className = "nodata";
      d.style.textAlign = "left";
      d.textContent = "Борт выполняет команду";
      box.appendChild(d);
    }

    const un = document.createElement("button");
    un.className = "btn";
    un.style.marginTop = "8px";
    un.innerHTML =
      '<span class="dot" style="background:#7f97b8"></span>СНЯТЬ ВЫДЕЛЕНИЕ';
    un.addEventListener("click", () => {
      this.session.select(null);
      this.renderCard(true);
    });
    card.appendChild(un);
  }

  private askConfirm(text: string, onYes: () => void): void {
    this.pendingConfirm = onYes;
    const t = $("#cfText", this.root);
    if (t) t.textContent = text;
    $("#ovConfirm", this.root)?.classList.remove("hide");
    const g = this.session.g;
    if (g) g.running = false;
  }

  private resolveConfirm(ok: boolean): void {
    $("#ovConfirm", this.root)?.classList.add("hide");
    const fn = this.pendingConfirm;
    this.pendingConfirm = null;
    const g = this.session.g;
    if (g && !g.over && !g.decision) g.running = true;
    if (ok && fn) fn();
  }

  endGame(title: string, sub: string): void {
    const g = this.session.g;
    if (!g || g.over) return;
    g.over = true;
    g.running = false;
    this.session.select(null);
    this.clearTutTimer();
    $("#tut", this.root)?.classList.add("hide");
    this.clearOverlays();
    const mode = g.modeId;
    const best = bestScore(mode);
    if (g.score > best) saveBest(g.score, mode);
    if (g.cfg.endless) {
      const meta = endlessRec();
      meta.waves = Math.max((meta.waves as number) || 0, g.front.wave);
      meta.score = Math.max((meta.score as number) || 0, g.score);
      saveEndlessMeta(meta);
    }
    this.updateBestUI();
    const served = g.landings + g.departures;
    const avg = g.waitCount ? g.waitSum / g.waitCount : 0;
    const gate = MODES[mode].scoreGate;
    let rank = "Стажер";
    if (g.score > 900 * gate) rank = "Диспетчер";
    if (g.score > 1800 * gate) rank = "Руководитель смены";
    if (g.score > 2800 * gate) rank = "Начальник оперативного штаба";
    if (g.score > 4000 * gate && g.minSafety >= 45 && g.violations === 0)
      rank = "Повелитель снегопада";
    if (g.violations >= MAX_VIOL || g.safety <= 0) rank = "Стажер";

    const set = (sel: string, v: string | number): void => {
      const el = $(sel, this.root);
      if (el) el.textContent = String(v);
    };
    set("#ovTitle", title);
    set("#ovSub", sub);
    set("#ovScore", g.score);
    set("#ovGrade", `Ранг: ${rank}`);
    set("#ovMode", MODES[mode].label);
    set("#rLand", served);
    set("#rDep", g.departures);
    set("#rWait", `${Math.round(avg)}с`);
    set("#rSafe", g.minSafety);
    set("#rDelay", g.delays);
    set("#rRisk", g.riskyOps);
    set("#rWave", g.cfg.endless ? g.front.wave : "—");
    set("#rViol", g.violations);
    set("#rStreak", g.bestStreak);

    const debrief = buildDebrief(g);
    const fillList = (
      sel: string,
      lines: ReturnType<typeof buildDebrief>["pressure"],
    ): void => {
      const ul = $(sel, this.root);
      if (!ul) return;
      ul.innerHTML = "";
      if (!lines.length) {
        const li = document.createElement("li");
        li.className = "good";
        li.innerHTML =
          "<b>Без заметного давления</b><span>Смена прошла спокойно по этому блоку</span>";
        ul.appendChild(li);
        return;
      }
      for (const line of lines) {
        const li = document.createElement("li");
        li.className = line.tone;
        li.innerHTML = `<b>${escHtml(line.title)}</b><span>${escHtml(line.detail)}</span>`;
        ul.appendChild(li);
      }
    };
    fillList("#ovPressure", debrief.pressure);
    fillList("#ovScoreNotes", debrief.scoreNotes);
    set("#ovBestMove", debrief.best);
    set("#ovLesson", debrief.lesson);
    set("#ovStory", debrief.summary);
    set("#ovTip", debrief.tip);
    set("#ovBest", bestScore(mode));
    const scLabel = g.scenarioLabel ? ` · ${g.scenarioLabel}` : "";
    set(
      "#shareTxt",
      `Аэропорт: Снежный фронт — ${rank}, ${g.score} очков, ${served} рейсов (${MODES[mode].label}${scLabel}). Мин. безопасность ${g.minSafety}. Сможешь лучше?`,
    );
    $("#ovOver", this.root)?.classList.remove("hide");
    Snd.tone(420, 0.2, "triangle", 0.1);
  }

  private pauseGame(v: boolean): void {
    const g = this.session.g;
    if (!g || g.over) return;
    if (v) {
      g.running = false;
      $("#ovPause", this.root)?.classList.remove("hide");
    } else {
      $("#ovPause", this.root)?.classList.add("hide");
      if (!g.decision && !this.pendingConfirm) g.running = true;
    }
  }

  private bindHelpNav(): void {
    const nav = $("#helpNav", this.root);
    if (!nav || (nav as HTMLElement & { _bound?: boolean })._bound) return;
    (nav as HTMLElement & { _bound?: boolean })._bound = true;
    nav.querySelectorAll<HTMLButtonElement>("[data-help]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.help;
        if (!id) return;
        Snd.click();
        this.showHelpPane(id);
      });
    });
  }

  private showHelpPane(id: string): void {
    const root = this.root;
    root.querySelectorAll("#helpNav [data-help]").forEach((el) => {
      el.classList.toggle("on", (el as HTMLElement).dataset.help === id);
    });
    root.querySelectorAll("[data-help-pane]").forEach((el) => {
      el.classList.toggle(
        "on",
        (el as HTMLElement).dataset.helpPane === id,
      );
    });
    const body = root.querySelector(".helpbook-body");
    if (body) body.scrollTop = 0;
  }

  private openHelp(): void {
    Snd.init();
    Snd.click();
    this.showHelpPane("play");
    $("#ovHelp", this.root)?.classList.remove("hide");
    const g = this.session.g;
    if (g?.running && !g.over) g.running = false;
  }

  private closeHelp(): void {
    Snd.click();
    $("#ovHelp", this.root)?.classList.add("hide");
    const g = this.session.g;
    const r = this.root;
    if (
      g &&
      !g.over &&
      !g.decision &&
      !this.pendingConfirm &&
      $("#ovPause", r)?.classList.contains("hide") &&
      $("#ovLog", r)?.classList.contains("hide") &&
      $("#ovMenu", r)?.classList.contains("hide") &&
      $("#ovOver", r)?.classList.contains("hide")
    ) {
      g.running = true;
    }
  }

  private syncLogModal(): void {
    const box = $("#logModal", this.root);
    if (!box) return;
    box.innerHTML = "";
    if (!this.logItems.length) {
      box.innerHTML = '<div class="nodata">В журнале пока нет сообщений</div>';
      return;
    }
    for (const item of this.logItems) {
      const copy = item.cloneNode(true) as HTMLElement;
      copy.style.opacity = "";
      copy.style.transition = "";
      box.appendChild(copy);
    }
  }

  private openLog(): void {
    Snd.init();
    Snd.click();
    this.syncLogModal();
    $("#ovLog", this.root)?.classList.remove("hide");
    const g = this.session.g;
    if (g?.running && !g.over) g.running = false;
  }

  private closeLog(): void {
    Snd.click();
    $("#ovLog", this.root)?.classList.add("hide");
    const g = this.session.g;
    const r = this.root;
    if (
      g &&
      !g.over &&
      !g.decision &&
      !this.pendingConfirm &&
      $("#ovPause", r)?.classList.contains("hide") &&
      $("#ovHelp", r)?.classList.contains("hide") &&
      $("#ovMenu", r)?.classList.contains("hide") &&
      $("#ovOver", r)?.classList.contains("hide")
    ) {
      g.running = true;
    }
  }

  private tutShow(): void {
    const g = this.session.g;
    const tut = $("#tut", this.root);
    if (!g || g.tutStep < 0 || g.tutStep >= TUT.length) {
      tut?.classList.add("hide");
      return;
    }
    tut?.classList.remove("hide");
    const n = $("#tutN", this.root);
    const t = $("#tutT", this.root);
    if (n) n.textContent = String(Math.min(g.tutStep + 1, 4));
    if (t) t.innerHTML = TUT[g.tutStep]!;
  }

  private tutNext(): void {
    const g = this.session.g;
    if (!g) return;
    g.tutStep++;
    if (g.tutStep >= TUT.length - 1) {
      this.tutShow();
      this.clearTutTimer();
      const token = g;
      this.tutTimer = setTimeout(() => {
        this.tutTimer = null;
        if (
          this.session.g === token &&
          token.mode === "tutorial" &&
          !token.over
        ) {
          this.startMatch(false);
        }
      }, 2600);
      return;
    }
    this.tutShow();
    Snd.tone(760, 0.1, "triangle", 0.08);
  }

  private clearTutTimer(): void {
    if (this.tutTimer) {
      clearTimeout(this.tutTimer);
      this.tutTimer = null;
    }
  }

  private clearOverlays(): void {
    for (const s of [
      "#ovEvent",
      "#ovConfirm",
      "#ovPause",
      "#ovOver",
      "#ovHelp",
      "#ovLog",
    ]) {
      $(s, this.root)?.classList.add("hide");
    }
    this.pendingConfirm = null;
    const g = this.session.g;
    if (g) g.decision = null;
  }

  private clearToasts(): void {
    const q = $("#log", this.root);
    if (q) q.innerHTML = "";
    const modal = $("#logModal", this.root);
    if (modal) modal.innerHTML = "";
    this.logItems = [];
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      if (!$("#ovLog", this.root)?.classList.contains("hide")) {
        this.closeLog();
        return;
      }
      if (!$("#ovHelp", this.root)?.classList.contains("hide")) {
        this.closeHelp();
        return;
      }
      const g = this.session.g;
      if (g && !g.over) this.pauseGame(g.running);
    }
    if (e.key === " " || e.key === "?") {
      if (e.key === "?") {
        e.preventDefault();
        this.openHelp();
        return;
      }
      e.preventDefault();
      const g = this.session.g;
      if (g && !g.over) this.pauseGame(g.running);
    }
  };

  private onVis = (): void => {
    const g = this.session.g;
    if (document.hidden && g?.running && !g.over) this.pauseGame(true);
  };
}
